import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Download, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import TechnologyUploadModal from '../components/technology/community/TechnologyUploadModal';
import {
  listAllJobTreadImports,
  uploadTemplatesImportFile,
  getFileKindFromRow,
  getImportGroupEmojis,
} from '../services/templatesImportsService';
import {
  filterResourcesBySearch,
  downloadRemoteFile,
  JOBTREAD_IMPORT_TYPES,
} from '../services/technologyCommunityService';
import PortalBackground from '../components/PortalBackground';
import { templateImportFileName } from '../utils/templateImportFileName';

/**
 * Import card matching the Sales Materials / HR Materials card style:
 * emoji tile + title + description with a single full-width Download action.
 */
const ImportCard = ({ row, emoji }) => {
  const { darkMode } = useTheme();
  const isJson = getFileKindFromRow(row) === 'json';
  // Docs created before version history lack the field — the CRM treats those as v1.0.
  const version = String(row.version || '').trim() || '1.0';

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-md ${
        darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
          {emoji ? (
            <span className="text-2xl leading-none">{emoji}</span>
          ) : (
            <FileText className="w-5 h-5" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {row.title || row.fileName}
            </h4>
            {version && (
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                  darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
                }`}
              >
                v{version}
              </span>
            )}
          </div>
          {(row.description || row.fileName) && (
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {row.description || row.fileName}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => downloadRemoteFile(row.downloadURL, templateImportFileName(row))}
        disabled={!row.downloadURL}
        title={row.downloadURL ? `Download ${isJson ? 'JSON' : 'CSV'} file` : 'File not available yet'}
        className={
          row.downloadURL
            ? 'w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors'
            : `w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border cursor-not-allowed ${
                darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-200 text-gray-300'
              }`
        }
      >
        <Download className="w-3.5 h-3.5" />
        Download
      </button>
    </div>
  );
};

const JobTreadImports = ({ embedded: embeddedProp }) => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const groupFilter = searchParams.get('type') || 'all';
  const highlightParam = searchParams.get('highlight');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [groupEmojis, setGroupEmojis] = useState({});
  const [highlightId, setHighlightId] = useState(null);

  // Deep link (?highlight=<id>) from What's New: scroll to the card and flash a ring.
  useEffect(() => {
    if (!highlightParam) return undefined;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`jt-import-${highlightParam}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(highlightParam);
      }
    }, 350);
    const clearTimer = setTimeout(() => setHighlightId(null), 3200);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightParam, rows]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const [list, emojis] = await Promise.all([
        listAllJobTreadImports(),
        getImportGroupEmojis(),
      ]);
      setRows(list);
      setGroupEmojis(emojis);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // CRM-picked group emojis override the defaults.
  const groups = useMemo(
    () => JOBTREAD_IMPORT_TYPES.map((t) => ({ ...t, emoji: groupEmojis[t.id] || t.emoji })),
    [groupEmojis]
  );

  // Import groups become sales-materials-style sections: the dropdown narrows
  // to one group and the search filters cards within each group.
  const visibleSections = useMemo(() => {
    const searched = filterResourcesBySearch(rows, searchTerm);
    return groups
      .filter((t) => groupFilter === 'all' || t.id === groupFilter)
      .map((t) => ({ ...t, items: searched.filter((r) => (r.tabId ?? r.importType) === t.id) }))
      .filter((s) => s.items.length > 0);
  }, [rows, searchTerm, groupFilter, groups]);

  const handleGroupChange = (value) => {
    if (value === 'all') setSearchParams({});
    else setSearchParams({ type: value });
  };

  const handleUpload = async (form) => {
    if (!currentUser) throw new Error('You must be logged in to upload.');
    setSubmitting(true);
    try {
      await uploadTemplatesImportFile({
        tabId: form.importType,
        title: form.title,
        description: form.description,
        file: form.file,
        userId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown',
        source: 'community',
        skipBootcampMirror: true,
      });
      await loadRows();
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div className={embeddedProp === false ? 'relative p-6 min-h-screen text-gray-900 dark:text-white' : ''}>
      {embeddedProp === false && <PortalBackground />}
      <div className={embeddedProp === false ? 'relative' : ''}>
      {/* Search + import group filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search imports..."
            className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
          />
        </div>
        <select
          value={groupFilter}
          onChange={(e) => handleGroupChange(e.target.value)}
          className={`sm:w-56 px-3 py-2 ${inputBase}`}
        >
          <option value="all">All groups</option>
          {groups.map((t) => (
            <option key={t.id} value={t.id}>
              {t.emoji ? `${t.emoji} ` : ''}{t.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowUpload(true)}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Share Import
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <BBALoader size={40} />
        </div>
      ) : visibleSections.length === 0 ? (
        <div
          className={`rounded-xl border p-8 text-center ${
            darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
          }`}
        >
          <FileText className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {rows.length === 0 ? 'No imports yet' : 'No imports match your search'}
          </h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {rows.length === 0
              ? 'Share the first JobTread import with the community.'
              : 'Try a different search or group.'}
          </p>
        </div>
      ) : (
        visibleSections.map((section) => (
          <div key={section.id} className="mt-10 first:mt-0">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                {section.emoji ? (
                  <span className="text-xl leading-none">{section.emoji}</span>
                ) : (
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
              </span>
              <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {section.label}
              </h2>
              <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {section.items.map((row) => (
                <div
                  key={row.id}
                  id={`jt-import-${row.id}`}
                  className={`rounded-xl transition-shadow ${
                    highlightId === row.id ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                  }`}
                >
                  <ImportCard row={row} emoji={row.emoji || section.emoji} />
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <TechnologyUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        type="jobtread-import"
        onSubmit={handleUpload}
        submitting={submitting}
      />
      </div>
    </div>
  );
};

export default JobTreadImports;
